import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { PlanPage } from "./planPage";
import { PlanForm } from "./planForm";
import { PlanView } from "./planView";
import { PlanService } from "./planService";
import { SubscriptionPlanForm } from "./subscription/subscriptionPlanForm";
import { SubscriptionService } from "../subscription/subscriptionService";

import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { apiErrorMessage } from "../../../assets/utils/lib/error";

import { PlanDto, SubscriptionPlanDto } from "../../../services/dto";
import { Dialog } from "../../../assets/vfx";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const PlanContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [gridActionType, setGridActionType] = useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const [data, setData] = useState<PlanDto>();
  const [
    showSubscriptionDeactivateDialog,
    setShowSubscriptionDeactivateDialog,
  ] = useState<boolean>(false);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string | null>(
    null
  );

  const isEditMode: boolean = !!(data?.id && data.id !== Constants.emptyGuid);

  const planQuery = PlanService.getPlanQuery(data?.id ?? undefined);
  const subscriptionQuery = SubscriptionService.getAllSubscriptionsQuery();

  const { mutateAsync: savePlan } = PlanService.save();
  const { mutate: deActivatePlan } = PlanService.deActivatePlan();
  const { mutateAsync: saveSubscriptionPlan } = PlanService.saveSubscriptionPlan();
  const { mutate: deactivateSubscriptionPlan } =
    PlanService.deActivateSubscriptionPlan();

  const closeDrawer = (): void =>{
      setGridActionType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const refreshGrid = (options?: {
    refreshPlans?: boolean;
    refreshSubscriptions?: boolean;
  }): void => {
    const { refreshPlans = true, refreshSubscriptions = true } = options ?? {};

    if (refreshPlans) {
      isEditMode && planQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["planGridQuery"] });
    }

    if (refreshSubscriptions) {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionPlanGridQuery"],
      });
    }

    closeDrawer();
  };

  const handleApiResponse = (
    response: any,
    successMessage: string,
    refreshOptions?: { refreshPlans?: boolean; refreshSubscriptions?: boolean }
  ): void => {
    const errorMsg: string | null = apiErrorMessage(response.errorMessages);

    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid(refreshOptions);
    }
  };

  const handleSubmit = async (values: PlanDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message: string = isEditMode ? "Plan updated" : "Plan saved";

    return await savePlan(values, {
      onSuccess: (response) =>
        handleApiResponse(response, message, {
          refreshPlans: true,
          refreshSubscriptions: false,
        }),
    });
  };

  const handleDeactivate = async () => {
    const message: string = "Plan deactivated";

    deActivatePlan(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) =>
        handleApiResponse(response, message, {
          refreshPlans: true,
          refreshSubscriptions: false,
        }),
    });
  };

  const handleSubscriptionPlanDeactivate = (): void => {
    const message: string = "Subscription Plan deactivated";

    subscriptionPlanId &&
      deactivateSubscriptionPlan(subscriptionPlanId, {
        onSuccess: (response) =>
          handleApiResponse(response, message, {
            refreshPlans: false,
            refreshSubscriptions: true,
          }),
      });
  };

  const handleSubscriptionPlanSave = async (values: SubscriptionPlanDto) => {
    const message: string = "Subscription Plan saved";

    return await saveSubscriptionPlan(values, {
      onSuccess: (response) =>
        handleApiResponse(response, message, {
          refreshPlans: false,
          refreshSubscriptions: true,
        }),
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setGridActionType(actionType);
      setData({ id: dataId } as PlanDto);
      setSearchAction(actionType, dataId);
    }
  };

  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const handleEdit = (): void => {
    setData(planQuery.data);
    setGridActionType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, planQuery.data?.id);
  };

  const handleSubscriptionPlanCustomEvent = (
    actionType: string,
    dataId: string
  ): void => {
    if (actionType === GRID_ACTION_TYPES.DEACTIVATE) {
      setShowSubscriptionDeactivateDialog(true);
      setSubscriptionPlanId(dataId);
    }
  };

  const renderDrawerContent = () => {
    switch (gridActionType) {
      case GRID_ACTION_TYPES.NEW:
        return <PlanForm show onHide={closeDrawer} onSubmit={handleSubmit} />;

      case GRID_ACTION_TYPES.NEW_SIBLING:
        return (
          <SubscriptionPlanForm
            show
            plan={planQuery.data}
            subscriptions={subscriptionQuery.data ?? []}
            onHide={closeDrawer}
            onSave={handleSubscriptionPlanSave}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <PlanForm
            data={planQuery.data}
            loading={planQuery.isLoading}
            show={!!planQuery.data}
            onHide={closeDrawer}
            onSubmit={handleSubmit}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <PlanView
            data={planQuery.data}
            show={!!planQuery.data}
            onHide={closeDrawer}
            onEdit={handleEdit}
            onDeactivate={handleDeactivate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <PlanPage
        onCustomEvent={handleCustomEvent}
        onSubscriptionPlanCustomEvent={handleSubscriptionPlanCustomEvent}
      />
      {renderDrawerContent()}
      <Dialog
        contentText={"Are you sure you want to deactivate this subscription?"}
        contentTitle={"Deactivate"}
        onClose={() => setShowSubscriptionDeactivateDialog(false)}
        onConfirm={() => {
          setShowSubscriptionDeactivateDialog(false);
          handleSubscriptionPlanDeactivate();
        }}
        open={showSubscriptionDeactivateDialog}
        closeText={"No, Go Back"}
        confirmText={"Yes Continue"}
      />
    </>
  );
};
